import { Config } from '@/utils/config';

const RELOADS_STORAGE_KEY = 'vite-preload-reloads';

type ReloadStorage = Pick<Storage, 'getItem' | 'setItem'>;
type ReloadLimits = { cooldownMs: number; windowMs: number; maxInWindow: number };

const readRecentReloads = (storage: ReloadStorage, now: number, windowMs: number): number[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(storage.getItem(RELOADS_STORAGE_KEY) ?? '[]');
  } catch {
    // Unreadable value: treat as no history. The next write replaces it.
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((at): at is number => typeof at === 'number' && now - at < windowMs);
};

// A redeploy removes the old hashed chunks, so a tab opened before it fails to load them.
// Reloading fetches the current index.html and chunk names. Allow that once per cooldown, and
// at most maxInWindow times per window, so a later deploy in a long-lived tab can recover but a
// chunk that stays unreachable cannot keep reloading the page under the user's unsaved edits.
export const shouldReloadAfterPreloadError = (
  storage: ReloadStorage | null,
  now: number,
  { cooldownMs, windowMs, maxInWindow }: ReloadLimits,
): boolean => {
  if (!storage) return false;
  try {
    const recent = readRecentReloads(storage, now, windowMs);
    if (recent.length >= maxInWindow) return false;
    if (recent.some((at) => now - at < cooldownMs)) return false;
    storage.setItem(RELOADS_STORAGE_KEY, JSON.stringify([...recent, now]));
    return true;
  } catch {
    return false;
  }
};

const getSessionStorage = (): ReloadStorage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

// Deliberately does not call event.preventDefault(): Vite then keeps throwing the real
// "Failed to fetch dynamically imported module" error. Swallowing it makes the dynamic
// import resolve to undefined, which crashes every React.lazy with "reading 'default'".
export const installPreloadErrorRecovery = () => {
  const { COOLDOWN_MS, WINDOW_MS, MAX_IN_WINDOW } = Config.PRELOAD_RELOAD;
  window.addEventListener('vite:preloadError', () => {
    const shouldReload = shouldReloadAfterPreloadError(getSessionStorage(), Date.now(), {
      cooldownMs: COOLDOWN_MS,
      windowMs: WINDOW_MS,
      maxInWindow: MAX_IN_WINDOW,
    });
    if (shouldReload) window.location.reload();
  });
};
